import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  LINK_CONTACT_TO_MISSION,
  UNLINK_CONTACT_TO_MISSION,
  LINK_CONTACT_TO_SOCIETY,
  UNLINK_CONTACT_TO_SOCIETY,
  UPDATE_CONTACT,
  UPSERT_CONTACT,
  SEARCH_CONTACTS,
} from '@bloomays-lib/adapter.api-bloomer';
import type { ContactMissionRole, ContactSocietyRole, ContactSide } from '@bloomays-lib/types.shared';

import { cancel, notify } from '../../../../helpers/toastify';

/** Contact renvoyé par la recherche annuaire (`searchContacts`). */
export type ContactSearchResult = {
  recordId: string;
  fullname?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
  nameJob?: string;
  isBilling?: boolean;
  isOperational?: boolean;
};

/** Champs éditables d'un contact (`updateContact`). `recordId` obligatoire (pas de création). */
export type UpdateContactValues = {
  recordId: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
  nameJob?: string;
};

/** Champs de création d'un contact (`upsertContact` sans `recordId`). */
export type CreateContactValues = {
  firstname: string;
  lastname: string;
  email: string;
  nameJob: string;
  phoneNumber?: string;
};

/** Cible de liaison appliquée juste après la création d'un contact. */
export type ContactLinkTarget =
  | { level: 'mission'; role: ContactMissionRole; side: ContactSide }
  | { level: 'society'; societyId: string; role: ContactSocietyRole };

/**
 * Hook d'orchestration des mutations contacts du Tableau de bord V2 (TEC-3052) : liaison/déliaison
 * mission↔contact et société↔contact, mise à jour de contact, et recherche annuaire. Chaque action
 * affiche un toast (en cours / succès / erreur) puis déclenche `onDone` (refetch dashboard).
 */
export const useContactMutations = ({
  missionId,
  onDone,
}: {
  missionId: string;
  onDone?: () => void | Promise<unknown>;
}) => {
  const { t } = useTranslation(['notify']);

  const [linkMissionMut, { loading: linkingMission }] = useMutation(LINK_CONTACT_TO_MISSION);
  const [unlinkMissionMut, { loading: unlinkingMission }] = useMutation(UNLINK_CONTACT_TO_MISSION);
  const [linkSocietyMut, { loading: linkingSociety }] = useMutation(LINK_CONTACT_TO_SOCIETY);
  const [unlinkSocietyMut, { loading: unlinkingSociety }] = useMutation(UNLINK_CONTACT_TO_SOCIETY);
  const [updateContactMut, { loading: updating }] = useMutation(UPDATE_CONTACT);
  const [upsertContactMut, { loading: creating }] = useMutation(UPSERT_CONTACT);
  const [searchQuery, { data: searchData, loading: searching }] = useLazyQuery(SEARCH_CONTACTS, {
    fetchPolicy: 'network-only',
  });

  const busy = linkingMission || unlinkingMission || linkingSociety || unlinkingSociety || updating || creating;

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await fn();
      notify('success', t('updateSuccess', { ns: 'notify' }));
      await onDone?.();
      return true;
    } catch (e: any) {
      cancel();
      notify('error', e?.message || t('randomUpdateError', { ns: 'notify' }));
      return false;
    }
  };

  return {
    busy,
    searching,
    searchResults: (searchData?.searchContacts ?? []) as ContactSearchResult[],
    search: (query: string) => searchQuery({ variables: { query } }),
    linkToMission: (contactId: string, role: ContactMissionRole, side: ContactSide) =>
      run(() => linkMissionMut({ variables: { contactId, missionId, role, side } })),
    unlinkFromMission: (contactId: string, role: ContactMissionRole, side: ContactSide) =>
      run(() => unlinkMissionMut({ variables: { contactId, missionId, role, side } })),
    linkToSociety: (contactId: string, societyId: string, role: ContactSocietyRole) =>
      run(() => linkSocietyMut({ variables: { contactId, societyId, role } })),
    unlinkFromSociety: (contactId: string, societyId: string, role: ContactSocietyRole) =>
      run(() => unlinkSocietyMut({ variables: { contactId, societyId, role } })),
    updateContact: (values: UpdateContactValues) => run(() => updateContactMut({ variables: { input: values } })),
    createAndLink: (values: CreateContactValues, link: ContactLinkTarget) =>
      run(async () => {
        const res = await upsertContactMut({ variables: { input: values } });
        const recordId = res.data?.upsertContact?.recordId as string | undefined;
        if (!recordId) throw new Error('Création du contact impossible');
        if (link.level === 'society') {
          await linkSocietyMut({ variables: { contactId: recordId, societyId: link.societyId, role: link.role } });
        } else {
          await linkMissionMut({ variables: { contactId: recordId, missionId, role: link.role, side: link.side } });
        }
      }),
  };
};
